import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.scss';
import { size } from '@/helpers/lodash';
import { classes } from '@/helpers/styling';
import { share } from '@/helpers/share';
import { getArtistAndTitleForTrack } from '@/helpers/audio';
import Text, { FONT_SIZE_040, FONT_SIZE_050 } from '@/components/component-library/Text';
import ButtonWrapper from '@/components/component-library/Button/Wrapper';
import useAppFilter from '@/hooks/layout/useAppFilter';
import TrackInfoScroller from '@/components/component-library/PlayerBar/TrackInfoScroller';
import useMusicPlayerSeekerBar from '@/hooks/audio/useMusicPlayerSeekerBar';
import useActiveEpisode from '@/hooks/audio/useActiveEpisode';
import Span from '@/components/component-library/Span';
import useStoreTrackSkipsInLocalStorage from '@/hooks/audio/useStoreTrackSkipsInLocalStorage';
import { createHandleTooltip } from '@/helpers/tooltip';
import { truthyOrZero } from '@/helpers/math';
import useSimpleAppState from '@/hooks/state/useSimpleAppState';

const AUTOSCROLLER_PROPS = {
  autoscroll: true,
  infiniteScroll: true
};

const CurrentlyPlayingTrack = ({
  isOpen,
  className
}) => {
  const { appIsFiltered } = useAppFilter();
  const { volumeBarOpen } = useSimpleAppState();
  const {
    saveSkipInLocalStorage,
    maxSkipsAllowed,
    skipsRemaining,
    minutesUntilNextSkipAvailable
  } = useStoreTrackSkipsInLocalStorage();
  const {
    handleSkipTrack: onSkipTrack,
    currentTrack,
    currentTrackText,
    currentTrackIsHidden,
    current,
    nextTrack
  } = useMusicPlayerSeekerBar();
  const { episode } = useActiveEpisode();
  const { tracklist } = episode;

  const { artist, title } = getArtistAndTitleForTrack({ track: currentTrack });

  const handleCopyTrackToClipboard = useCallback(() => {
    share({
      url: currentTrackText,
      useShareApi: false,
      tooltipText: 'Song copied to clipboard'
    });
  }, [currentTrackText]);

  const handleSkipTrack = useCallback((e) => {
    e.stopPropagation();

    saveSkipInLocalStorage();
    onSkipTrack();
  }, [onSkipTrack, saveSkipInLocalStorage]);

  const handleWarnNoSkipsRemaining = useCallback((e) => {
    e.stopPropagation();

    const handleTooltip = createHandleTooltip({
      e,
      text: `More skips remaining countdown: ${minutesUntilNextSkipAvailable}`
    });

    handleTooltip();
  }, [minutesUntilNextSkipAvailable]);

  const tracklistHasAtLeastOneValidTrack = useMemo(() => !!(size(tracklist) && size(tracklist.filter((track) => track.end_time > track.start_time))), [tracklist]);
  const currentlyPlayingTrackIsKnown = truthyOrZero(currentTrack?.start_time);
  const displayCurrentTrackSection = !!(!volumeBarOpen && isOpen && tracklistHasAtLeastOneValidTrack);
  const nextTrackIsValid = truthyOrZero(nextTrack?.start_time);
  const nextTrackStartTimeIsInFuture = !!current && (nextTrack?.start_time >= current);
  const displaySkipTrackButton = !!(
    displayCurrentTrackSection
      && nextTrackIsValid
      && nextTrackStartTimeIsInFuture
  );
  const onNoSkipsRemaining = displaySkipTrackButton ? handleWarnNoSkipsRemaining : undefined;
  const onClickSkip = (displaySkipTrackButton && !!skipsRemaining) ? handleSkipTrack : onNoSkipsRemaining;
  const onClickTrack = (displayCurrentTrackSection && currentlyPlayingTrackIsKnown) ? handleCopyTrackToClipboard : undefined;

  const artistTOptions = useMemo(() => ({ artist }), [artist]);
  const skipsRemainingTOptions = useMemo(() => ({
    remaining: skipsRemaining,
    total: maxSkipsAllowed
  }), [skipsRemaining, maxSkipsAllowed]);

  return (
    <div
      className={ classes(
        styles.tracklistCurrentTrack,
        className,
        {
          [styles.isVisible]: displayCurrentTrackSection,
          [styles.isClickable]: !!onClickTrack,
          [styles.isFiltered]: appIsFiltered
        }
      ) }
      onClick={ onClickTrack }
    >
      <div className={ styles.trackScrollers }>
        <Text
          bold
          className={ styles.inTheMixTitle }
          size={ FONT_SIZE_040 }
        >
          In the Mix
        </Text>
        <TrackInfoScroller
          autoScrollerProps={ AUTOSCROLLER_PROPS }
          channelTitle={ (currentlyPlayingTrackIsKnown && artist && !currentTrackIsHidden) ? (
            <Span className={ styles.byText }>by {artist}</Span>
          ) : '' }
          className={ styles.currentArtistScroller }
          disableClicks
          disableGradients
          disableLibraryButton
          episodeClassName
          episodeTitle={ (
            <Span
              className={ styles.episodeTitle }
            >
              { (currentlyPlayingTrackIsKnown && title && !currentTrackIsHidden)
                ? title
                : 'Unknown Song' }
            </Span>
          ) }
        />
      </div>
      <div className={ classes(styles.skipSection, { [styles.isVisible]: displaySkipTrackButton }) }>
        <ButtonWrapper disabled={ !onClickSkip } disableHoverEffect onClick={ onClickSkip }>
          <Text
            bold className={ classes(styles.skipSongButton, { [styles.disabled]: !displaySkipTrackButton || !skipsRemaining }) }
            useHeaderFont
          >
            Skip Song
          </Text>
        </ButtonWrapper>
        <Text className={ classes(styles.remainingSkipsText, { [styles.isClickable]: !!onClickTrack }) } size={ FONT_SIZE_050 }>
          Skips remaining: {skipsRemaining}/{maxSkipsAllowed}
        </Text>
      </div>
    </div>
  );
};

CurrentlyPlayingTrack.propTypes = {
  isOpen: PropTypes.bool,
  className: PropTypes.string
};

CurrentlyPlayingTrack.defaultProps = {
  isOpen: false,
  className: ''
};

export default CurrentlyPlayingTrack;
